<template>
    <form class="modal-card custom-view-modal">
        <header class="modal-card-head">
            <p class="modal-card-title">{{ customView ? 'Edit' : 'Create' }} Custom View</p>
        </header>

        <section class="modal-card-body">
            <o-field
                    :message="getError('name')"
                    :type="fieldType('name')"
                    label="Name"
            >
                <o-input v-model="copy.name" placeholder="Name of the View"></o-input>
            </o-field>

            <div v-if="isCurrentUserAdmin" class="field">
                <o-checkbox
                        v-model="copy.is_public"
                        :true-value="1"
                        :false-value="0"
                >
                    Is Universal View
                </o-checkbox>
            </div>

            <hr>

            <!-- filters start -->
            <div class="filter-label">
                <strong>Filter</strong>
            </div>
            <div
                    v-for="(filter, i) in copy.filters"
                    :key="i"
                    class="columns column-filters"
            >
                <div class="column is-4-tablet">
                    <o-field
                            :type="fieldType(`filters.${i}.column`)"
                            :message="getError(`filters.${i}.column`)"
                    >
                        <o-select
                                v-model="filter.column"
                                :expanded="true"
                                placeholder="Select a Column"
                                @update:modelValue="setDefaultComparator(filter)"
                        >
                            <option disabled value="">Column</option>
                            <template v-for="(f, i) in filters" :key="i">
                                <option
                                        v-if="f.name"
                                        :value="f.field"

                                >
                                    {{ f.name }}
                                </option>
                            </template>
                        </o-select>
                    </o-field>
                </div>

                <div class="column is-3-tablet">
                    <o-field
                            :type="fieldType(`filters.${i}.comparator`)"
                            :message="getError(`filters.${i}.comparator`)"
                    >
                        <o-select
                                v-model="filter.comparator"
                                :expanded="true"
                                placeholder="Comparator"
                                @update:modelValue="checkComparator(filter)"
                        >
                            <option
                                    v-for="(comparator, i) in comparatorsByField(filter.column)"
                                    :value="comparator"
                                    :key="i"
                            >
                                {{ comparator }}
                            </option>
                        </o-select>
                    </o-field>
                </div>

                <div
                        v-if="showFilterValue(filter)"
                        :class="`column is-${copy.filters.length > 1 ? 4 : 5 }-tablet`"
                >
                    <o-field
                            :type="fieldType(`filters.${i}.value`)"
                            :message="getError(`filters.${i}.value`)"
                    >
                        <autocomplete-select
                                v-if="filterByField(filter.column).type === 'autocomplete'"
                                v-model="copy.filters[i].value"
                                :custom="{placeholder: 'Value'}"
                                :type="filterByField(filter.column).autocomplete_type"
                        />

                        <datepicker
                                v-else-if="filterByField(filter.column).type === 'date'"
                                v-model="copy.filters[i].value"

                                placeholder="Due Date"
                        />

                        <o-select
                                v-else-if="filterByField(filter.column).type === 'select'"
                                v-model="copy.filters[i].value"
                                placeholder="Select a Column"
                        >
                            <option disabled value="">Select Options</option>
                            <template>
                                <div v-for="option in filterByField(filter.column).options">
                                  <option
                                      :value="option.value"
                                      :key="option.value"
                                  >
                                    {{ option.text }}
                                  </option>
                                </div>
                            </template>
                        </o-select>

                        <o-select
                                v-else-if="filterByField(filter.column).type === 'bool'"
                                v-model="copy.filters[i].value"
                                placeholder="Select a Column"
                        >
                            <option disabled value="">Select Options</option>
                            <template>
                               <div v-for="option in boolOptions">
                                 <option
                                     :value="option.value"
                                     :key="option.value"
                                 >
                                   {{ option.text }}
                                 </option>
                               </div>
                            </template>
                        </o-select>

                        <o-input
                                v-else-if="filterByField(filter.column).type === 'search'"
                                v-model="copy.filters[i].value"
                                :type="filterByField(filter.column).type"
                                :placeholder="filterByField(filter.column).name"
                                icon="search">
                        </o-input>

                        <o-input
                                v-else
                                v-model="copy.filters[i].value"
                                placeholder="Value">
                        </o-input>
                    </o-field>
                </div>
                <div
                        v-if="copy.filters.length > 1"
                        class="column is-1-tablet has-text-right remove-filter-button"
                >
                    <a
                            href="#"
                            class="icon is-small pointer"
                            @click.prevent="removeFilter(i)"
                    >
                        <i class="fa fa-times-circle-o" aria-hidden="true"></i>
                    </a>
                </div>
            </div>
            <div class="columns">
                <div class="column">
                    <button @click.prevent="handleAddFilterBtn" class="button is-primary">Add filter</button>
                </div>
            </div>
            <!-- filters end -->

            <!-- columns start -->
            <div class="columns">
                <div class="column">
                    <o-field label="Available Columns" class="flex-column is-marginless">
                        <o-input
                                v-model="filteredColumnName"
                                class="table-column-input"
                                placeholder="Column Name"
                                type="search"
                                icon="search">
                        </o-input>
                    </o-field>
                    <ul class="table-column-list">
                        <li
                                v-for="column in filteredColumns"
                                :key="column.field"
                                :class="['pointer', {'active': copy.columns.includes(column.field)}]"
                                @click="toggleColumn(column.field)"
                        >
                            <i
                                    :class="['fa', 'fa-check', {'hidden': ! copy.columns.includes(column)}]"
                                    aria-hidden="true">
                            </i> {{ column.label }}
                        </li>
                    </ul>
                </div>

                <div class="column">
                    <o-field
                            :message="getError('columns')"
                            :type="fieldType('columns')"
                            label="Selected Columns"
                    >

                        <draggable v-if="copy.columns.length" group="people"  item-key="id" :model-value="copy.columns">
                          <template #item="{ element, index }">
                            <li
                            >
                              {{ columnNameByField(element) }}
                              <a
                                  href="#"
                                  class="icon is-small pointer float-right"
                                  @click.prevent="removeColumn(index)"
                              >
                                <i class="fa fa-times-circle-o" aria-hidden="true"></i>
                              </a>
                            </li>
                          </template>
                        </draggable>
                        <span v-else>When no specific columns are selected, by default all of them are shown</span>
                    </o-field>
                </div>
            </div>
            <!-- columns end -->
        </section>

        <footer class="modal-card-foot">
            <button
                    class="button"
                    type="button"
                   @click="$parent.$parent.$emit('update:active', false)">
                Close
            </button>
            <button
                    v-submit="deleteSubmitting"
                    v-if="customView"
                    class="button is-danger"
                    @click.prevent="remove"
            >
                Delete
            </button>
            <button
                    v-submit="submitting"
                    class="button is-primary"
                    @click.prevent="handleSubmit"
            >
                Save
            </button>
        </footer>
    </form>
</template>

<script>
    import HandlesForm from './../../mixins/HandlesForm'
    import {mapActions, mapGetters} from 'vuex'
    import Multiselect from '@vueform/multiselect'
    import Datepicker from './../Datepicker'
    import draggable from 'vuedraggable/src/vuedraggable'

    export default {
        components: {
            Multiselect, Datepicker, draggable,
        },

        mixins: [HandlesForm],

        props: {
            type: {
                default: 'projects',
                type: String
            },
            customView: {
                default: () => null,
                type: Object
            },
            includeDefaultColumns: {
                default: false,
                type: Boolean,
            }
        },

        data() {
            return {
                filteredColumnName: null,
                deleteSubmitting: false,

                copy: {
                    type: this.type,
                    name: null,
                    columns: [],
                    is_public: false,
                    created_by: null,
                    filters: [{
                        id: null,
                        column: null,
                        comparator: null,
                        value: null,
                        type: null
                    }],
                },

                boolOptions: [
                    {
                        text: 'True', value: 1
                    }, {
                        text: 'False', value: 0
                    }
                ]
            }
        },

        computed: {
            ...mapGetters([
                'allTableColumnsBySlug',
                'isCurrentUserAdmin',
                'tableBySlug',
                'tableColumnsBySlug',
                'tableColumnNames',
                'filterMappings',
                'currentUserId',
                'comparators',
                'companies',
                'contacts',
                'users',
            ]),

            checkComparator(e) {
                return filter => {
                        if(filter.comparator === 'is empty') {
                            filter.value = null
                        }
                    }
            },

            columnNameByField() {
                return field => _.find(this.columns, {field}).label
            },

            columns() {
                return this.allTableColumnsBySlug(this.copy.type + 'Index')
            },

            filteredColumns() {
                return this.filteredColumnName
                    ? _.reject(
                        this.columns,
                        column => column.label.toLowerCase().includes(this.filteredColumnName.toLowerCase()) !== true
                    )
                    : this.columns
            },

            filters() {
                return this.filterMappings[this.type]
            },

            filterByField() {
                return field => _.find(this.filters, {field})
            },

            comparatorsByField() {
                return field => {
                    let filter = this.filterByField(field)

                    return filter ? this.comparators[filter.type] : []
                }
            },

            showFilterValue() {
                return filter => filter.column && filter.comparator !== 'is empty';
            }
        },

        mounted() {
            const table = this.tableBySlug(this.copy.type + 'Index')
            const tableColumns = table.columns.map(c => c.field)
            if (this.customView) {
                const columns = this.customView.columns.filter(c => tableColumns.includes(c))
                const cv = { ...this.customView, columns }
                this.assignCustomView(cv)
                _.each(this.customView.filters, filter => this.addFilter(filter))
            } else if(this.includeDefaultColumns) {
                this.copy.columns = table.default
            }
        },

        methods: {
            ...mapActions(['createOrUpdateTableView', 'removeTableView']),

            assignCustomView(customView) {
                this.copy = {
                    id: customView.id,
                    type: customView.type,
                    name: customView.name,
                    columns: customView.columns,
                    is_public: customView.is_public,
                    created_by: customView.created_by,
                    filters: []
                }
            },

            setDefaultComparator(filter) {
                let comparators = this.comparatorsByField(filter.column)

                filter.type = this.filterByField(filter.column).type

                if (! comparators.includes(filter.comparator)) {
                    filter.comparator = comparators[0]
                }
            },

            addFilter(filter = null) {
                if (filter) {
                    let value = filter.value

                    return this.copy.filters.push({
                        id: filter.id,
                        column: filter.column,
                        comparator: filter.comparator,
                        value: !value || isNaN(value) ? value : +value,
                        type: filter.column ? this.filterByField(filter.column).type : null,
                    })
                }

                this.copy.filters.push({
                    id: null,
                    column: null,
                    comparator: null,
                    value: null,
                    type: null
                })
            },

            handleAddFilterBtn() {
                this.addFilter()
            },

            removeFilter(index) {
                this.copy.filters.splice(index, 1)
            },

            toggleColumn(column) {
                if (this.copy.columns.includes(column)) {
                    return this.copy.columns = _.remove(this.copy.columns, col => col !== column)
                }

                this.copy.columns.push(column)
            },

            removeColumn(index) {
                this.copy.columns.splice(index, 1)
            },

            remove() {
                this.deleteSubmitting = true
                let submit = this.wrapSubmit(this.removeTableView)

                submit(this.customView)
                    .then(res => {
                        this.deleteSubmitting = false
                        this.successMessage('Table view has been deleted')
                        this.$parent.$parent.$emit('update:active', false)
                        this.$emit('custom-view-deleted')
                        return res
                    })
            },

            handleSubmit() {
                let submit = this.wrapSubmit(this.createOrUpdateTableView)

                submit(this.copy)
                    .then(res => {
                        this.successMessage('Table view has been successfull updated')
                        this.$emit('custom-view-created', res.data.data.id)
                        this.$parent.$parent.$emit('update:active', false)
                        return res
                    })
            }
        }
    }
</script>
